/**
 * Exámenes de laboratorio dentro de la atención: pedirlos y ver resultados.
 *
 * Antes esto era una línea escrita a mano dentro del campo "Plan". Escrito así,
 * el laboratorio no se enteraba, caja no sabía qué cobrar y el resultado volvía
 * en un papel que nadie adjuntaba a la historia.
 *
 * Al elegir un examen se ve su preparación —"ayuno de 8 horas"— porque es lo
 * que hay que decirle al paciente ANTES de que se vaya, y si necesita cita se
 * avisa ahí mismo: eso no se atiende por orden de llegada.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Pencil, Plus, Search, Trash2, TriangleAlert } from "lucide-react";
import {
  type Examen,
  type OrdenLab,
  anularOrden,
  crearOrden,
  listExamenes,
  listOrdenes,
} from "../../api/laboratorio";
import { formatApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";
import { AnularDialog } from "./AnularDialog";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../ui";

const soles = (v: string | number | null | undefined) =>
  `S/ ${Number(v ?? 0).toFixed(2)}`;

const ESTADO_LABEL: Record<string, { label: string; variant: "neutral" | "success" | "warning" | "danger" }> = {
  SOLICITADA: { label: "Por cobrar", variant: "warning" },
  EN_PROCESO: { label: "En proceso", variant: "warning" },
  CON_RESULTADOS: { label: "Con resultados", variant: "success" },
  ENTREGADA: { label: "Entregada", variant: "neutral" },
  ANULADA: { label: "Anulada", variant: "danger" },
};

export function OrdenLaboratorioEpisodio({
  episodioId,
  cerrada = false,
  permitirAgregar = false,
}: {
  episodioId: number;
  /** La atención está firmada: no nace nada nuevo salvo que se pida aparte. */
  cerrada?: boolean;
  /** El médico pulsó "Añadir a esta atención" y sabe lo que hace. */
  permitirAgregar?: boolean;
}) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<OrdenLab | null>(null);
  const [anulando, setAnulando] = useState<OrdenLab | null>(null);
  const bloqueado = cerrada && !permitirAgregar;
  const [errorAnular, setErrorAnular] = useState<string | null>(null);

  const anular = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      anularOrden(id, motivo),
    onSuccess: () => {
      setAnulando(null);
      setErrorAnular(null);
      queryClient.invalidateQueries({ queryKey: ["lab"] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
    onError: (e) => setErrorAnular(formatApiError(e, "No se pudieron anular los exámenes.")),
  });

  const puedeSolicitar = me?.permisos.includes("laboratorio:solicitar") ?? false;
  const puedeVer = me?.permisos.includes("laboratorio:read") ?? false;

  const { data: ordenes = [] } = useQuery({
    queryKey: ["lab", "ordenes", "episodio", episodioId],
    queryFn: () => listOrdenes({ episodio_id: episodioId }),
    enabled: puedeVer,
  });

  if (!puedeVer && !puedeSolicitar) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <FlaskConical
                className="h-4 w-4 text-[color:var(--color-link)]"
                aria-hidden="true"
              />
              Exámenes de laboratorio
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Se cobran en caja y el laboratorio los procesa. Los resultados
              vuelven a esta ficha.
            </p>
          </div>
          {/* Igual que la receta: una orden por atención.
 
              Con una ya pedida, el botón llevaba a crear otra y el paciente
              acababa con dos órdenes que cobrar por separado en caja. Si falta
              o sobra un examen, se corrige la que hay.

              Reaparece cuando la orden ya está en proceso: el laboratorio la
              tomó, no se puede corregir, y si el médico necesita otro examen no
              le queda más remedio que pedirlo aparte. */}
          {puedeSolicitar && !bloqueado &&
            ordenes.every((o) => o.estado !== "SOLICITADA") && (
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" />
              {ordenes.length === 0 ? "Solicitar exámenes" : "Solicitar otros exámenes"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {ordenes.length === 0 && (
          <p className="text-sm text-slate-400">
            Sin exámenes solicitados en esta atención.
          </p>
        )}
        {ordenes.map((o) => (
          <div key={o.id} className="space-y-1">
            <OrdenCard orden={o} />
            {/* Solo mientras el laboratorio no la haya empezado. Con la
                muestra ya tomada, anular la orden no deshace nada: se corrige
                pidiendo otra. */}
            {puedeSolicitar && !bloqueado && o.estado === "SOLICITADA" && (
              <div className="flex justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setCorrigiendo(o)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Corregir
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setErrorAnular(null);
                    setAnulando(o);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Anular
                </button>
              </div>
            )}
          </div>
        ))}
      </CardBody>

      <AnularDialog
        open={anulando != null}
        onClose={() => setAnulando(null)}
        titulo="Anular estos exámenes"
        queSeAnula={
          anulando ? `Orden #${anulando.id} · ${anulando.items.length} examen(es)` : ""
        }
        loading={anular.isPending}
        error={errorAnular}
        onConfirm={(motivo) => anulando && anular.mutate({ id: anulando.id, motivo })}
      />

      {(open || corrigiendo) && (
        <SolicitarDialog
          episodioId={episodioId}
          cerrada={cerrada}
          corrige={corrigiendo}
          onClose={() => {
            setOpen(false);
            setCorrigiendo(null);
          }}
          onSaved={() => {
            setOpen(false);
            setCorrigiendo(null);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ["lab"] });
            queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
          }}
          onError={setError}
        />
      )}
    </Card>
  );
}

/** Una orden con sus exámenes y, si los hay, sus resultados. */
export function OrdenCard({ orden }: { orden: OrdenLab }) {
  const tz = useZonaHoraria();
  const ui = ESTADO_LABEL[orden.estado] ?? { label: orden.estado, variant: "neutral" as const };
  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">Orden #{orden.id}</span>
        <Badge variant={ui.variant}>{ui.label}</Badge>
        {orden.urgente && <Badge variant="danger">Urgente</Badge>}
        {orden.requiere_agenda && (
          <Badge variant="warning">Requiere cita en admisión</Badge>
        )}
        <span className="text-xs text-slate-500">{fechaHora(orden.fecha, tz)}</span>
        <span className="ml-auto font-mono text-sm text-slate-700">
          {soles(orden.total)}
          {orden.pagada ? (
            <span className="ml-2 text-xs text-emerald-700">pagada</span>
          ) : (
            <span className="ml-2 text-xs text-amber-700">por cobrar</span>
          )}
        </span>
      </div>

      {orden.diagnostico_presuntivo && (
        <p className="mt-1 text-xs text-slate-500">
          Presuntivo: {orden.diagnostico_presuntivo}
        </p>
      )}

      <ul className="mt-2 divide-y divide-slate-50">
        {orden.items.map((i) => (
          <li key={i.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5 text-sm">
            <span className="text-slate-800">{i.descripcion}</span>
            {i.tiene_resultado ? (
              <>
                <span
                  className={`font-mono ${
                    i.fuera_de_rango ? "font-semibold text-red-600" : "text-slate-900"
                  }`}
                >
                  {i.valor ?? "informe"} {i.unidad ?? ""}
                </span>
                {i.valor_referencia && (
                  <span className="text-xs text-slate-400">ref. {i.valor_referencia}</span>
                )}
                {i.fuera_de_rango && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                    <TriangleAlert className="h-3 w-3" />
                    fuera de rango
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-slate-400">
                {i.preparacion ? `Pendiente · ${i.preparacion}` : "Pendiente"}
              </span>
            )}
          </li>
        ))}
      </ul>

      {orden.items.some((i) => i.texto) && (
        <div className="mt-2 space-y-1">
          {orden.items
            .filter((i) => i.texto)
            .map((i) => (
              <div key={`t-${i.id}`} className="rounded bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {i.descripcion}
                </p>
                <p className="whitespace-pre-wrap text-sm text-slate-800">{i.texto}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function SolicitarDialog({
  episodioId,
  cerrada = false,
  corrige,
  onClose,
  onSaved,
  onError,
}: {
  episodioId: number;
  /** Se pide sobre una atención ya firmada: hay que confirmarlo. */
  cerrada?: boolean;
  /** La orden que se corrige: se emite otra con lo que quede y esta se anula. */
  corrige?: OrdenLab | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [q, setQ] = useState("");
  const [elegidos, setElegidos] = useState<Examen[]>([]);
  const [presuntivo, setPresuntivo] = useState(
    corrige?.diagnostico_presuntivo ?? "",
  );
  const [urgente, setUrgente] = useState(corrige?.urgente ?? false);
  const [precargado, setPrecargado] = useState(false);

  const { data: examenes = [] } = useQuery({
    queryKey: ["lab", "examenes"],
    queryFn: () => listExamenes(),
  });

  // Los exámenes de la orden que se corrige, resueltos contra el catálogo:
  // la orden guarda `examen_id` y descripción, pero el selector trabaja con
  // el objeto entero (precio, área). Se espera a que el catálogo llegue.
  if (corrige && !precargado && examenes.length > 0) {
    setPrecargado(true);
    const ids = new Set(corrige.items.map((i) => i.examen_id));
    setElegidos(examenes.filter((e) => ids.has(e.id)));
  }

  const filtrados = examenes.filter((e) => {
    if (elegidos.some((x) => x.id === e.id)) return false;
    if (q.trim().length < 2) return true;
    const t = `${e.nombre} ${e.codigo} ${e.area ?? ""}`.toLowerCase();
    return t.includes(q.trim().toLowerCase());
  });

  const total = elegidos.reduce((s, e) => s + Number(e.precio), 0);

  const guardar = useMutation({
    mutationFn: async () => {
      const nueva = await crearOrden({
        episodio_id: episodioId,
        confirmar_posterior: cerrada,
        examen_ids: elegidos.map((e) => e.id),
        diagnostico_presuntivo: presuntivo.trim() || null,
        urgente,
      });
      // Igual que en la receta: primero la nueva, después retirar la vieja.
      if (corrige) {
        await anularOrden(corrige.id, `Corregida: reemplazada por la orden #${nueva.id}`);
      }
      return nueva;
    },
    onSuccess: onSaved,
    onError: (e) =>
      onError(
        formatApiError(
          e,
          corrige ? "No se pudo corregir la orden." : "No se pudo crear la orden.",
        ),
      ),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={corrige ? `Corregir la orden #${corrige.id}` : "Solicitar exámenes"}
      description={
        corrige
          ? "Sale una orden nueva con lo que dejes aquí y la anterior queda anulada."
          : "Se cobran en caja y pasan al laboratorio."
      }
      size="lg"
    >
      <div className="space-y-4">
        {elegidos.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {elegidos.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <span className="text-sm text-slate-900">{e.nombre}</span>
                {e.tipo_muestra && (
                  <span className="text-xs text-slate-500">{e.tipo_muestra}</span>
                )}
                {e.preparacion && (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
                    {e.preparacion}
                  </span>
                )}
                {e.requiere_cita && (
                  <Badge variant="warning">Requiere cita</Badge>
                )}
                <span className="ml-auto font-mono text-sm text-slate-700">
                  {soles(e.precio)}
                </span>
                <button
                  type="button"
                  onClick={() => setElegidos(elegidos.filter((x) => x.id !== e.id))}
                  className="text-slate-400 hover:text-red-600"
                  aria-label="Quitar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <Field label="Buscar examen" htmlFor="lab_q">
          <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              id="lab_q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Hemograma, glucosa, orina…"
              className="w-full border-0 p-0 text-sm focus:outline-none"
            />
          </div>
        </Field>

        <ul className="max-h-56 divide-y divide-slate-50 overflow-auto rounded-lg border border-slate-200">
          {filtrados.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => setElegidos([...elegidos, e])}
                className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
              >
                <span className="text-sm text-slate-900">{e.nombre}</span>
                <span className="text-xs text-slate-400">{e.area}</span>
                {e.horas_resultado != null && (
                  <span className="text-xs text-slate-400">
                    resultado en {e.horas_resultado} h
                  </span>
                )}
                <span className="ml-auto font-mono text-xs text-slate-500">
                  {soles(e.precio)}
                </span>
              </button>
            </li>
          ))}
          {filtrados.length === 0 && (
            <li className="px-3 py-4 text-center text-sm text-slate-400">
              Sin exámenes que coincidan.
            </li>
          )}
        </ul>

        <Field
          label="Diagnóstico presuntivo"
          htmlFor="lab_dx"
          hint="Qué se sospecha. El laboratorio no interpreta, pero un valor raro se revisa distinto sabiéndolo."
        >
          <Input
            id="lab_dx"
            value={presuntivo}
            onChange={(e) => setPresuntivo(e.target.value)}
            placeholder="Descartar anemia"
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={urgente}
            onChange={(e) => setUrgente(e.target.checked)}
          />
          Urgente — va primero en la cola del laboratorio
        </label>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">
              Total a cobrar
            </p>
            <p className="font-mono text-xl font-bold text-slate-900">{soles(total)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => guardar.mutate()}
              disabled={elegidos.length === 0 || guardar.isPending}
            >
              Solicitar {elegidos.length > 0 && `(${elegidos.length})`}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
